import { Injectable } from '@angular/core';
import { isNullOrUndefined } from '../shared/common-utils';

export const WORKFLOW_STORAGE_KEYS = {
  originalStationData: 'originalStationData',
  surveyData: 'surveyData',
  inputJson: 'inputJson',
  stationData: 'stationData',
  mergedTagTinData: 'merged_tag_tin_data'
} as const;

export type WorkflowStorageKey = typeof WORKFLOW_STORAGE_KEYS[keyof typeof WORKFLOW_STORAGE_KEYS];

interface WorkflowStorageRecord<T = unknown> {
  key: WorkflowStorageKey;
  value: T;
  updatedAt: number;
}

@Injectable({ providedIn: 'root' })
export class WorkflowIndexedDbService {
  private static readonly databaseName = 'KavachWorkflowDB';
  private static readonly databaseVersion = 1;
  private static readonly storeName = 'workflowState';
  private static readonly storageKeys = Object.values(WORKFLOW_STORAGE_KEYS);

  private static databasePromise: Promise<IDBDatabase | null> | null = null;
  private static mutationQueue: Promise<void> = Promise.resolve();

  private readonly cache = new Map<WorkflowStorageKey, unknown>();
  private readonly revisions = new Map<WorkflowStorageKey, number>();
  private initializationPromise?: Promise<void>;

  initialize(): Promise<void> {
    if (!this.initializationPromise) {
      this.initializationPromise = this.initializeInternal();
    }
    return this.initializationPromise;
  }

  get<T>(key: WorkflowStorageKey): T | null {
    if (!this.cache.has(key)) {
      return null;
    }
    return this.clone(this.cache.get(key) as T);
  }

  set<T>(key: WorkflowStorageKey, value: T): void {
    const storedValue = this.clone(value);
    this.incrementRevision(key);
    this.cache.set(key, storedValue);

    void this.enqueueMutation(async () => {
      const database = await this.openDatabase();
      if (database) {
        await this.putRecord(database, key, storedValue);
      }
    }).catch(error => console.error(`Failed to persist ${key} in IndexedDB`, error));
  }

  remove(key: WorkflowStorageKey): void {
    this.incrementRevision(key);
    this.cache.delete(key);

    void this.enqueueMutation(async () => {
      const database = await this.openDatabase();
      if (database) {
        await this.runWriteTransaction(database, store => store.delete(key));
      }
    }).catch(error => console.error(`Failed to remove ${key} from IndexedDB`, error));
  }

  async clear(): Promise<void> {
    for (const key of WorkflowIndexedDbService.storageKeys) {
      this.incrementRevision(key);
    }
    this.cache.clear();

    await this.enqueueMutation(async () => {
      const database = await this.openDatabase();
      if (database) {
        await this.runWriteTransaction(database, store => store.clear());
      }
    });
  }

  async whenIdle(): Promise<void> {
    await WorkflowIndexedDbService.mutationQueue;
  }

  private async initializeInternal(): Promise<void> {
    if (!this.isBrowser()) {
      return;
    }

    const revisionsAtStart = new Map(this.revisions);

    try {
      await WorkflowIndexedDbService.mutationQueue;
      const database = await this.openDatabase();
      if (!database) {
        return;
      }

      const records = await this.getAllRecords(database);
      const persistedKeys = new Set<WorkflowStorageKey>();

      for (const record of records) {
        persistedKeys.add(record.key);
        if (this.getRevision(record.key) === (revisionsAtStart.get(record.key) ?? 0)) {
          this.cache.set(record.key, this.clone(record.value));
        }
      }

      await this.migrateLegacyLocalStorage(database, persistedKeys, revisionsAtStart);
    } catch (error) {
      console.error('Failed to initialize workflow data from IndexedDB', error);
      this.loadLegacyValuesIntoMemory(revisionsAtStart);
    }
  }

  private async migrateLegacyLocalStorage(
    database: IDBDatabase,
    persistedKeys: Set<WorkflowStorageKey>,
    revisionsAtStart: Map<WorkflowStorageKey, number>
  ): Promise<void> {
    for (const key of WorkflowIndexedDbService.storageKeys) {
      const revisionAtStart = revisionsAtStart.get(key) ?? 0;
      if (revisionAtStart > 0 || this.getRevision(key) !== revisionAtStart) {
        this.removeLegacyValue(key);
        continue;
      }

      if (persistedKeys.has(key)) {
        this.removeLegacyValue(key);
        continue;
      }

      const legacyValue = this.readLegacyValue(key);
      if (legacyValue === null) {
        continue;
      }

      await this.putRecord(database, key, legacyValue);
      if (this.getRevision(key) === revisionAtStart) {
        this.cache.set(key, this.clone(legacyValue));
      }
      this.removeLegacyValue(key);
    }
  }

  private loadLegacyValuesIntoMemory(revisionsAtStart: Map<WorkflowStorageKey, number>): void {
    for (const key of WorkflowIndexedDbService.storageKeys) {
      const legacyValue = this.readLegacyValue(key);
      if (legacyValue !== null && this.getRevision(key) === (revisionsAtStart.get(key) ?? 0)) {
        this.cache.set(key, legacyValue);
      }
    }
  }

  private readLegacyValue(key: WorkflowStorageKey): unknown | null {
    let value: string | null;
    try {
      value = localStorage.getItem(key);
    } catch (error) {
      console.error(`Failed to read legacy ${key} from local storage`, error);
      return null;
    }

    if (value === null) {
      return null;
    }

    try {
      return JSON.parse(value);
    } catch (error) {
      console.error(`Failed to migrate ${key} from local storage`, error);
      return null;
    }
  }

  private removeLegacyValue(key: WorkflowStorageKey): void {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.error(`Failed to remove legacy ${key} from local storage`, error);
    }
  }

  private getAllRecords(database: IDBDatabase): Promise<WorkflowStorageRecord[]> {
    return new Promise<WorkflowStorageRecord[]>((resolve, reject) => {
      try {
        const transaction = database.transaction(WorkflowIndexedDbService.storeName, 'readonly');
        const request = transaction.objectStore(WorkflowIndexedDbService.storeName).getAll();
        request.onsuccess = () => resolve(request.result as WorkflowStorageRecord[]);
        request.onerror = () => reject(request.error ?? new Error('Failed to read workflow data from IndexedDB'));
      } catch (error) {
        reject(error);
      }
    });
  }

  private putRecord<T>(database: IDBDatabase, key: WorkflowStorageKey, value: T): Promise<void> {
    return this.runWriteTransaction(database, store => {
      const record: WorkflowStorageRecord<T> = {
        key,
        value: this.clone(value),
        updatedAt: Date.now()
      };
      store.put(record);
    });
  }

  private enqueueMutation(operation: () => Promise<void>): Promise<void> {
    const result = WorkflowIndexedDbService.mutationQueue.then(operation, operation);
    WorkflowIndexedDbService.mutationQueue = result.catch(() => undefined);
    return result;
  }

  private openDatabase(): Promise<IDBDatabase | null> {
    if (!this.isBrowser()) {
      return Promise.resolve(null);
    }

    if (WorkflowIndexedDbService.databasePromise) {
      return WorkflowIndexedDbService.databasePromise;
    }

    WorkflowIndexedDbService.databasePromise = new Promise<IDBDatabase>((resolve, reject) => {
      const request = indexedDB.open(
        WorkflowIndexedDbService.databaseName,
        WorkflowIndexedDbService.databaseVersion
      );

      request.onupgradeneeded = () => {
        const database = request.result;
        if (!database.objectStoreNames.contains(WorkflowIndexedDbService.storeName)) {
          database.createObjectStore(WorkflowIndexedDbService.storeName, { keyPath: 'key' });
        }
      };

      request.onsuccess = () => {
        const database = request.result;
        database.onversionchange = () => {
          database.close();
          WorkflowIndexedDbService.databasePromise = null;
        };
        resolve(database);
      };

      request.onerror = () => {
        WorkflowIndexedDbService.databasePromise = null;
        reject(request.error ?? new Error('Failed to open workflow IndexedDB'));
      };

      request.onblocked = () => {
        WorkflowIndexedDbService.databasePromise = null;
        reject(new Error('Opening workflow IndexedDB was blocked'));
      };
    });

    return WorkflowIndexedDbService.databasePromise;
  }

  private runWriteTransaction(
    database: IDBDatabase,
    operation: (store: IDBObjectStore) => void
  ): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      try {
        const transaction = database.transaction(WorkflowIndexedDbService.storeName, 'readwrite');
        transaction.oncomplete = () => resolve();
        transaction.onerror = () => reject(transaction.error ?? new Error('IndexedDB transaction failed'));
        transaction.onabort = () => reject(transaction.error ?? new Error('IndexedDB transaction was aborted'));
        operation(transaction.objectStore(WorkflowIndexedDbService.storeName));
      } catch (error) {
        reject(error);
      }
    });
  }

  private incrementRevision(key: WorkflowStorageKey): void {
    this.revisions.set(key, this.getRevision(key) + 1);
  }

  private getRevision(key: WorkflowStorageKey): number {
    return this.revisions.get(key) ?? 0;
  }

  private clone<T>(value: T): T {
    if (!isNullOrUndefined(value)) {
      return value;
    }
    return structuredClone(value);
  }

  private isBrowser(): boolean {
    return typeof window !== 'undefined' && typeof indexedDB !== 'undefined' && typeof localStorage !== 'undefined';
  }
}
